import { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { useCustomAppClient } from 'api/custom-client';
import { getProposalContextSignature, isRecord } from 'app/modules/wallforming/components/copilot/utils/copilotUtils';

import type {
    CopilotChatRequest,
    CopilotChatResponse,
    CopilotChatState,
    CopilotConversation
} from 'app/modules/wallforming/components/copilot/types/copilotTypes';

interface UseCopilotMessageSenderProps {
    activeConversation: CopilotConversation | undefined;
    buildRequest: (prompt: string) => CopilotChatRequest | null;
    isOpen: boolean;
    isSending: boolean;
    prompt: string;
    setChatState: Dispatch<SetStateAction<CopilotChatState>>;
    setError: Dispatch<SetStateAction<string | null>>;
    setIsSending: Dispatch<SetStateAction<boolean>>;
    setPrompt: Dispatch<SetStateAction<string>>;
}

export const useCopilotMessageSender = ({
    activeConversation,
    buildRequest,
    isOpen,
    isSending,
    prompt,
    setChatState,
    setError,
    setIsSending,
    setPrompt
}: UseCopilotMessageSenderProps) => {
    const { t } = useTranslation();
    const postChatTurn = useCustomAppClient<CopilotChatResponse>();
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!isOpen) {
            abortControllerRef.current?.abort();
        }
    }, [isOpen]);

    const cancelMessage = useCallback(() => abortControllerRef.current?.abort(), []);

    const sendMessage = useCallback(async () => {
        const trimmedPrompt = prompt.trim();
        if (!trimmedPrompt || isSending || !activeConversation) {
            return;
        }

        const conversationId = activeConversation.id;
        let request: CopilotChatRequest | null;
        try {
            request = buildRequest(trimmedPrompt);
        } catch {
            setError(t('copilotContextBuildError'));
            return;
        }
        if (!request) {
            setError(t('copilotSelectContextError'));
            return;
        }

        const userMessage = { role: 'user' as const, content: trimmedPrompt };
        setChatState((current) => ({
            ...current,
            conversations: current.conversations.map((conversation) =>
                conversation.id === conversationId
                    ? {
                          ...conversation,
                          title:
                              conversation.messages.length === 0
                                  ? trimmedPrompt.length > 36
                                      ? `${trimmedPrompt.slice(0, 36)}...`
                                      : trimmedPrompt
                                  : conversation.title,
                          messages: [...conversation.messages, userMessage]
                      }
                    : conversation
            )
        }));
        setPrompt('');
        setError(null);
        setIsSending(true);
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
            const response = await postChatTurn({
                url: '/copilot/chat',
                method: 'POST',
                data: request,
                signal: abortController.signal
            });
            const contextSignature = getProposalContextSignature(request.context);
            setChatState((current) => ({
                ...current,
                conversations: current.conversations.map((conversation) =>
                    conversation.id === conversationId
                        ? {
                              ...conversation,
                              messages: [
                                  ...conversation.messages,
                                  {
                                      role: 'assistant',
                                      content: response.message,
                                      ...(response.tokenUsage ? { tokenUsage: response.tokenUsage } : {}),
                                      proposals: (response.proposals ?? []).map((proposal) => ({
                                          ...proposal,
                                          id: uuidv4(),
                                          contextSignature
                                      }))
                                  }
                              ]
                          }
                        : conversation
                )
            }));
        } catch (requestError: unknown) {
            if (!abortController.signal.aborted) {
                const response = isAxiosError<unknown>(requestError) ? requestError.response : undefined;
                const problem = response?.data;
                const detail = isRecord(problem) && typeof problem.detail === 'string' ? problem.detail.trim() : '';
                setError(detail || t('copilotRequestError'));
                setPrompt((current) => current || trimmedPrompt);
                setChatState((current) => ({
                    ...current,
                    conversations: current.conversations.map((conversation) =>
                        conversation.id === conversationId
                            ? {
                                  ...conversation,
                                  messages: conversation.messages.filter((message) => message !== userMessage)
                              }
                            : conversation
                    )
                }));
            } else {
                setChatState((current) => ({
                    ...current,
                    conversations: current.conversations.map((conversation) => {
                        const lastMessage = conversation.messages[conversation.messages.length - 1];
                        return conversation.id === conversationId &&
                            lastMessage?.role === 'user' &&
                            lastMessage.content === trimmedPrompt
                            ? { ...conversation, messages: conversation.messages.slice(0, -1) }
                            : conversation;
                    })
                }));
            }
        } finally {
            abortControllerRef.current = null;
            setIsSending(false);
        }
    }, [
        activeConversation,
        buildRequest,
        isSending,
        postChatTurn,
        prompt,
        setChatState,
        setError,
        setIsSending,
        setPrompt,
        t
    ]);

    return { cancelMessage, sendMessage };
};
